import { useEffect, useRef } from "react";
import { collection, getDocs, onSnapshot } from "firebase/firestore";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { db } from "@/config/firebase";
import { AuthStore } from "@/store/useAuthStore";

export const useRealTimeListeners = () => {
  const queryClient = useQueryClient();
  const notificationsInitialized = useRef(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      const { setNotifications } = AuthStore.getState();

      try {
        // Fetch all notifications and requests initially
        const notificationsSnapshot = await getDocs(
          collection(db, "notificationsMain"),
        );
        const requestsSnapshot = await getDocs(collection(db, "requestsMain"));

        const notifications: Array<any> = [];
        const requests: Array<any> = [];

        notificationsSnapshot.forEach((doc) => {
          notifications.push({ id: doc.id, ...doc.data() });
        });

        requestsSnapshot.forEach((doc) => {
          requests.push({ id: doc.id, ...doc.data() });
        });

        // Count unread and read notifications
        const unreadNotifications = notifications.filter(
          (notification) => !notification.seen,
        ).length;
        const readNotifications = notifications.filter(
          (notification) => notification.seen,
        ).length;

        // Count pending and processed requests
        const pendingRequests = requests.filter(
          (request) => request.status === "pending",
        ).length;
        const processedRequests = requests.filter(
          (request) => request.status !== "pending",
        ).length;

        // Update the notifications and requests count in the store
        setNotifications({
          unread: unreadNotifications,
          read: readNotifications,
          pendingRequests,
          processedRequests,
        });

        // Invalidate and refetch queries if necessary
        const hasNewFarmerNotification = notifications.some(
          (notification) => notification.action === "Farmer Registered",
        );

        if (hasNewFarmerNotification) {
          queryClient.removeQueries({ queryKey: ["allFarmers"] });
          queryClient.refetchQueries({ queryKey: ["allFarmers"] });
        }
      } catch (error) {
        console.error("Error fetching initial data: ", error);
      }
    };

    const setupRealTimeListeners = () => {
      const { setNotifications } = AuthStore.getState();

      try {
        // Set up real-time listener for notifications
        const unsubscribeNotifications = onSnapshot(
          // use notifications for dev and notificationsMain for prod
          collection(db, "notificationsMain"),
          (querySnapshot) => {
            const notifications: Array<any> = [];

            querySnapshot.forEach((doc) => {
              notifications.push({ id: doc.id, ...doc.data() });
            });

            const unreadNotifications = notifications.filter(
              (notification) => !notification.seen,
            ).length;
            const readNotifications = notifications.filter(
              (notification) => notification.seen,
            ).length;

            // Update the notifications count in the store
            setNotifications((prev) => ({
              ...prev,
              unread: unreadNotifications,
              read: readNotifications,
            }));

            // The admin API writes animals to MongoDB, so consume the
            // corresponding Firestore broadcast and invalidate every animal
            // query family in this browser session. Skip the initial snapshot
            // (whose changes are all historical documents).
            if (notificationsInitialized.current) {
              for (const change of querySnapshot.docChanges()) {
                if (change.type !== "added") continue;
                const notification = change.doc.data() as {
                  action?: string;
                  animalId?: string;
                  farmerId?: string | null;
                };
                if (notification.action !== "Animal Updated" || !notification.animalId) continue;

                void queryClient.refetchQueries({ queryKey: ["allAnimals"], type: "all" });
                void queryClient.refetchQueries({ queryKey: ["animals"], type: "all" });
                void queryClient.refetchQueries({
                  queryKey: ["SilaAnimal", notification.animalId],
                  type: "all",
                });
                if (notification.farmerId) {
                  void queryClient.refetchQueries({
                    queryKey: ["farmerOwnedAnimals", notification.farmerId],
                    type: "all",
                  });
                }
              }
            }
            notificationsInitialized.current = true;

            const hasNewFarmerNotification = notifications.some(
              (notification) => notification.action === "Farmer Registered",
            );

            if (hasNewFarmerNotification) {
              toast.info("New Farmer has Registered");
              queryClient.refetchQueries({
                queryKey: ["allFarmers"],
              });
            }
          },
        );

        // Set up real-time listener for requests
        const unsubscribeRequests = onSnapshot(
          // use requests for dev and requestsMain for prod
          collection(db, "requestsMain"),
          (querySnapshot) => {
            const requests: Array<any> = [];

            querySnapshot.forEach((doc) => {
              toast.info("New Animal Details Update Request");
              requests.push({ id: doc.id, ...doc.data() });
            });

            const pendingRequests = requests.filter(
              (request) => request.status === "pending",
            ).length;
            const processedRequests = requests.filter(
              (request) => request.status !== "pending",
            ).length;

            // Update the requests count in the store
            setNotifications((prev) => ({
              ...prev,
              pendingRequests,
              processedRequests,
            }));
          },
        );

        // Cleanup both listeners on unmount
        return () => {
          unsubscribeNotifications();
          unsubscribeRequests();
        };
      } catch (error) {
        console.error("Error setting up real-time listeners: ", error);
      }
    };

    // Step 1: Fetch all notifications and requests initially
    fetchInitialData();

    // Step 2: Set up real-time listeners for notifications and requests
    const unsubscribe = setupRealTimeListeners();

    // Cleanup the real-time listeners on unmount
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [queryClient]);
};
